import { PerformanceAudienceFilterOption, PerformanceAudienceSummary } from '@/services/endpoints/performanceAudience';
import { SelectOption } from '@/lib/Selects/Select/types';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRealAccountOptions } from '../../utils/buildCampaignPerformanceDashboardArgs';
import { DATE_RANGE_CUSTOM_ID, findFilterOption } from '../../utils/filterOptions';
import { formatDisplayDateRange } from '../../utils/resolveDateRangePreset';
import { AudienceFilterSelect } from '../AudienceFilterSelect';
import { FilterPillSelect } from '../FilterPillSelect';
import { FilterSummaryLine } from '../FilterSummaryLine';
import { MetaScopeMultiSelect } from '../MetaScopeMultiSelect';
import { CalendarFilterIcon, GoogleAdsIcon } from '../PlatformBrandIcons';
import { twMerge } from 'tailwind-merge';

type PerformanceAudienceFilterCardProps = {
  isRefetching?: boolean;
  summary?: PerformanceAudienceSummary;
  audiences: PerformanceAudienceFilterOption[];
  googleScopes: PerformanceAudienceFilterOption[];
  metaScopes: PerformanceAudienceFilterOption[];
  dateRanges: PerformanceAudienceFilterOption[];
  selectedAudienceId: string;
  selectedGoogleScopeId: string;
  selectedMetaScopeIds: string[];
  selectedDateRangeId: string;
  selectedDateFrom?: string;
  selectedDateTo?: string;
  onAudienceChange: (option: SelectOption) => void;
  onGoogleScopeChange: (option: SelectOption) => void;
  onMetaScopeIdsChange: (ids: string[]) => void;
  onDateRangeChange: (option: SelectOption) => void;
  onCustomDateRangeApply: (range: { from: string; to: string }) => void;
};

export const PerformanceAudienceFilterCard: FC<PerformanceAudienceFilterCardProps> = ({
  isRefetching = false,
  summary,
  audiences,
  googleScopes,
  metaScopes,
  dateRanges,
  selectedAudienceId,
  selectedGoogleScopeId,
  selectedMetaScopeIds,
  selectedDateRangeId,
  selectedDateFrom,
  selectedDateTo,
  onAudienceChange,
  onGoogleScopeChange,
  onMetaScopeIdsChange,
  onDateRangeChange,
  onCustomDateRangeApply,
}) => {
  const { t, i18n } = useTranslation('performanceAudience');

  const selectedAudience = findFilterOption(audiences, selectedAudienceId);
  const selectedGoogleScope = findFilterOption(googleScopes, selectedGoogleScopeId);
  const selectedDateRange = findFilterOption(dateRanges, selectedDateRangeId);

  const customRangeLabel =
    selectedDateRangeId === DATE_RANGE_CUSTOM_ID && selectedDateFrom && selectedDateTo
      ? formatDisplayDateRange(selectedDateFrom, selectedDateTo, i18n.language)
      : undefined;

  const googleCount = summary?.googleAccountCount ?? (googleScopes.filter((o) => !o.id.endsWith('-all')).length || (selectedGoogleScope ? 1 : 0));
  const metaAccounts = getRealAccountOptions(metaScopes);
  const metaCount = summary?.metaAdAccountCount ?? (selectedMetaScopeIds.length || metaAccounts.length);

  const scopeSummary: PerformanceAudienceSummary | null = summary
    ? summary
    : selectedAudience
      ? {
          audienceName: selectedAudience.value,
          audienceUserCount: 0,
          googleAccountCount: googleCount,
          metaAdAccountCount: metaCount,
          googleCampaignCount: 0,
          metaAdSetCount: 0,
        }
      : null;

  const handleAudienceApply = useCallback(
    (id: string) => {
      const audience = audiences.find((item) => item.id === id);
      if (!audience) {
        return;
      }
      onAudienceChange({ id: audience.id, label: audience.value });
    },
    [audiences, onAudienceChange]
  );

  return (
    <div
      className={twMerge(
        'flex flex-col gap-[18px] rounded-[18px] border border-ink-150 bg-white p-[26px] shadow-[0_1px_2px_rgba(11,23,51,0.04),0_4px_16px_rgba(11,23,51,0.04)]',
        isRefetching && 'pointer-events-none opacity-60'
      )}
      aria-busy={isRefetching}
    >
      <div className='flex flex-wrap items-center gap-2'>
        <AudienceFilterSelect items={audiences} selectedId={selectedAudienceId} onApply={handleAudienceApply} disabled={!audiences.length} />
        <FilterPillSelect
          icon={<GoogleAdsIcon />}
          categoryLabel={t('filters.googleAds')}
          selected={selectedGoogleScope}
          items={googleScopes}
          onChange={onGoogleScopeChange}
          accountCountSuffix={googleCount ? `· ${googleCount} account` : undefined}
          disabled={!googleScopes.length}
          disabledTooltipSource='google'
        />
        <MetaScopeMultiSelect items={metaScopes} selectedIds={selectedMetaScopeIds} onApply={onMetaScopeIdsChange} disabled={!metaAccounts.length} />
        <FilterPillSelect
          icon={<CalendarFilterIcon />}
          selected={selectedDateRange}
          items={dateRanges}
          onChange={onDateRangeChange}
          variant='date'
          customRangeLabel={customRangeLabel}
          customDateFrom={selectedDateFrom}
          customDateTo={selectedDateTo}
          onCustomDateRangeApply={onCustomDateRangeApply}
        />
      </div>
      {scopeSummary ? <FilterSummaryLine summary={scopeSummary} /> : null}
    </div>
  );
};
